import {
  InventoryType,
  SuitLevelInventory,
  SuitLevelV2Inventory,
} from "@/global/store/data/types";
import {
  AnimatedCard,
  AnimatedCardProps,
} from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { Box, Button, Divider, Stack, Theme, Typography } from "@mui/material";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { InventoryTag } from "./inventoryCard.helpers";
import { InventoryCardListItem } from "./inventoryCard.components";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useDispatch } from "react-redux";
import { seatMapState } from "@/global/store/data/seatMap";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

interface Props {
  cardVariant?: AnimatedCardProps["variant"];
  inventory: InventoryType;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledHoverAnim?: boolean;
  isFooterHidden?: boolean;
  onSelect?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRender?: (el: HTMLElement) => void;
  style?: AnimatedCardProps["style"];
}

export function SuiteLevelInventoryCard(props: Props) {
  const dispatch = useDispatch();
  const inventory = props.inventory as SuitLevelInventory;
  const teamConfig = useTeamConfig();
  const { inventoryCardSettings } = teamConfig.uiConfigs;
  const { suiteImages } = teamConfig.seatMapConfigs;
  const { sm: smDown } = useCssMediaQuery("down");

  const inventoryCardSettingValue =
    typeof inventoryCardSettings?.inventorySuitePackageInfo == "string"
      ? inventoryCardSettings?.inventorySuitePackageInfo
      : inventoryCardSettings?.inventorySuitePackageInfo?.find(
          (itm) =>
            itm.suiteId ===
            ((props.inventory as SuitLevelV2Inventory).suiteName ||
              props.inventory.id)
        )?.FBPackageValue || "Included";

  let inventoryCardListItemValue = inventoryCardSettings?.pbLineItemsValue
    ? String(inventoryCardSettings?.pbLineItemsValue)
    : "3";

  //! hard coded for event 24SSV928 and suite STE27, Jira https://seasonshare.atlassian.net/jira/software/projects/ENG/boards/3?assignee=712020%3A576b12a3-81f4-4fae-afc3-7c9987e706fd&selectedIssue=ENG-1391
  if (inventory.eventCode == "24SSV928" && inventory.id === "STE27") {
    inventoryCardListItemValue = "4";
  }

  return (
    <AnimatedCard
      variant={props?.cardVariant}
      style={props?.style}
      onRender={props?.onRender}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      borderRadius={"10px"}
      footerHeight={"70px"}
      isSelected={props?.isSelected}
      isDisabled={props?.isDisabled}
      isDisabledHoverAnim={props?.isDisabledHoverAnim}
      isFooterHidden={props?.isFooterHidden}
      renderBody={() => {
        return (
          <Box onClick={props.onSelect}>
            <Stack
              sx={{
                flexDirection: "row",
                position: "absolute",
                top: 0,
                right: "8px",
                zIndex: 1,
                transform: "translate(0%, -50%) scale(1)",
                overflow: "hidden",
                gap: 1,
              }}
            >
              {inventory?.containsAisle ? (
                <InventoryTag value={"Aisle Seat"} />
              ) : null}

              {inventory.sectionNameTag ? (
                <InventoryTag value={inventory.sectionNameTag} />
              ) : null}
            </Stack>

            <Box p={2} py={2.2}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                >
                  {inventory?.suiteName
                    ? `${inventory.suiteName}`
                    : `Section ${inventory.sectionName}`}
                </Typography>
                {/* <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                >
                  {getUSCurrency(Number(inventory.price))}
                </Typography> */}

                {!!suiteImages ? (
                  <Button
                    sx={{
                      py: 0,
                      px: 1,
                      fontSize: smDown ? "0.75rem" : "0.875rem",
                    }}
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(
                        seatMapState.actions.set({
                          viewId3d: `S_${inventory?.mapSectionName}`,
                        })
                      );
                    }}
                  >
                    {inventoryCardSettings?.viewSuiteButtonLabel ||
                      "View Suite"}
                  </Button>
                ) : null}
              </Stack>
            </Box>
            <Divider />

            <Stack padding={"1rem"}>
              <InventoryCardListItem
                label={inventoryCardSettings?.seatCountLabel || "Seat Count: "}
                value={inventory.seatCount}
              />

              {inventoryCardSettings?.isPBLineItemsVisible ? (
                <>
                  <InventoryCardListItem
                    label="Parking Passes: "
                    value={inventoryCardListItemValue}
                  />
                  <InventoryCardListItem
                    label="F&B Package: "
                    value={inventoryCardSettingValue}
                  />
                </>
              ) : null}

              <InventoryCardListItem
                label="Total Price:"
                value={getUSCurrency(Number(inventory.totalPrice))}
              />
            </Stack>
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Box width={"100%"} p={2}>
            <Button
              variant={props.isSelected ? "outlined" : "contained"}
              onClick={props.onSelect}
              sx={{ width: "100%" }}
            >
              {props.isSelected ? "Unselect" : "Select +"}
            </Button>
          </Box>
        );
      }}
    />
  );
}

function useStyles(theme: Theme) {
  return {
    inventoryTagWrapper: {
      position: "absolute",
      top: 0,
      right: "8px",
      zIndex: 1,
      transform: "translate(0%, -50%) scale(1)",
      overflow: "hidden",
    },
  };
}
